import { useState, useCallback } from "react";
import { ChevronDown, Clock, FolderOpen, Image as ImageIcon, ImageOff } from "lucide-react";
import { cn, formatFile } from "@/modern/utils";
import { useMarketingContext } from "../context/MarketingContext";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { MarketingAsset } from "@/bryntum-lib/models/MarketingAsset";
import { MarketingTag } from "@/bryntum-lib/models/MarketingTag";
import { EntityFileProvider, FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { FileDetailModal } from "@/react/components/entityViews/files/components/editors/FileDetailModal/FileDetailModal";
import { useEntityManagerContext } from "@/react/components/entityManager/context/EntityManagerContext";

export const AssetThumbnail = ({ asset, className, onClick }: { asset: MarketingAsset; className?: string; onClick?: () => void }) => {
  const { project } = useSwivuBryntumContext();
  const fileRecord = project?.boardFilesStore?.getById(asset.file) as any;
  const thumbnailUrl = fileRecord?.url || fileRecord?.thumbnailUrl;

  return (
    <div
      className={cn(
        "relative w-20 h-20 rounded-md overflow-hidden bg-muted border border-border group cursor-pointer",
        className,
      )}
      onClick={onClick}
    >
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={asset.altText || asset.caption}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="w-6 h-6 text-muted-foreground" />
        </div>
      )}
    </div>
  );
};

const CollapsibleSection = ({
  icon,
  label,
  count,
  children,
  variant = "default",
  defaultOpen = false,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  children: React.ReactNode;
  variant?: "default" | "temporal";
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors",
          variant === "temporal"
            ? "bg-primary/5 hover:bg-primary/10"
            : "bg-card hover:bg-muted/50"
        )}
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-foreground">{label}</span>
          <span className="text-xs text-primary font-semibold">{count}</span>
        </div>
        <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="px-3 py-3 border-t border-border bg-card">
          {children}
        </div>
      )}
    </div>
  );
};

const FolderSection = ({ tag, assets, onAssetClick }: { tag: MarketingTag; assets: MarketingAsset[]; onAssetClick?: (asset: MarketingAsset) => void }) => {
  return (
    <CollapsibleSection
      icon={<FolderOpen className="w-4 h-4 text-muted-foreground" />}
      label={tag.label}
      count={assets.length}
    >
      <div className="flex flex-wrap gap-2">
        {assets.map((asset) => (
          <AssetThumbnail key={asset.id} asset={asset} onClick={onAssetClick ? () => onAssetClick(asset) : undefined} />
        ))}
      </div>
    </CollapsibleSection>
  );
};

export const MarketingAssetsContent = () => {
  const { totalApproved, temporalGroups, folderGroups } = useMarketingContext();
  const { project, user } = useSwivuBryntumContext();
  const { record: entityRecord } = useEntityManagerContext();

  const [fileDetailFile, setFileDetailFile] = useState<FormattedFile | null>(null);
  const [isFileDetailOpen, setIsFileDetailOpen] = useState(false);

  const handleAssetClick = useCallback(
    (asset: MarketingAsset) => {
      const boardFile = project?.boardFilesStore?.getById(asset.file);
      if (!boardFile) return;
      const [formatted] = formatFile([boardFile], user);
      setFileDetailFile(formatted);
      setIsFileDetailOpen(true);
    },
    [project, user],
  );

  if (totalApproved === 0) {
    return (
      <div className="min-h-[12rem] h-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 p-4 text-center shadow-sm m-4">
        <div className="mx-auto flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ImageOff className="size-5" />
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-foreground">No marketing assets</h3>
          <p className="text-xs text-muted-foreground">Promote files from the file viewer to see them here.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-3 space-y-4">
        {/* Temporal Classification */}
        {temporalGroups.length > 0 && (
          <div>
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">Temporal Classification</span>
            <div className="space-y-2">
              {temporalGroups.map((group) => (
                <CollapsibleSection
                  key={group.tag.id}
                  icon={<Clock className="w-4 h-4 text-primary" />}
                  label={group.tag.label}
                  count={group.assets.length}
                  variant="temporal"
                >
                  <div className="flex flex-wrap gap-2">
                    {group.assets.map((asset) => (
                      <AssetThumbnail key={asset.id} asset={asset} onClick={() => handleAssetClick(asset)} />
                    ))}
                  </div>
                </CollapsibleSection>
              ))}
            </div>
          </div>
        )}

        {/* Content Role */}
        {folderGroups.length > 0 && (
          <div>
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">Content Roles</span>
            <div className="space-y-2">
              {folderGroups.map((group) => (
                <FolderSection key={group.tag.id} tag={group.tag} assets={group.assets} onAssetClick={handleAssetClick} />
              ))}
            </div>
          </div>
        )}
      </div>

      {fileDetailFile && (
        <EntityFileProvider record={entityRecord} entityType={entityRecord.entityType}>
          <FileDetailModal
            entityRecord={entityRecord}
            file={fileDetailFile}
            open={isFileDetailOpen}
            setOpen={setIsFileDetailOpen}
            autoOpenPromotePanel
          />
        </EntityFileProvider>
      )}
    </>
  );
};
